import React, { useState, useEffect, useRef } from 'react';
import {
  Package, Search, Filter, Plus, Edit3, Trash2, Image as ImageIcon,
  Store, Percent, RefreshCw, CheckCircle2, AlertCircle, X, ChevronLeft,
  ChevronRight, Globe, Layers, DollarSign, Thermometer, ShieldCheck,
  Upload, UploadCloud, HardDrive, Link as LinkIcon, Loader2, Camera, Check,
  Eye, Sparkles, Tag, Boxes, FileText, CheckCircle, AlertTriangle, Calendar,
  Award, ExternalLink, ArrowUpRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageHelper';
import masterCatalogService, {
  MasterProduct, CatalogStats, TaxRule
} from '../../services/admin/masterCatalogService';

const MasterCatalogManagement: React.FC = () => {
  // Main Data States
  const [products, setProducts] = useState<MasterProduct[]>([]);
  const [stats, setStats] = useState<CatalogStats | null>(null);
  const [taxRules, setTaxRules] = useState<TaxRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination & Filter States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTaxCategory, setSelectedTaxCategory] = useState('');
  const [selectedTempType, setSelectedTempType] = useState('');

  // Product Details Modal State
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [viewingProduct, setViewingProduct] = useState<MasterProduct | null>(null);
  const [selectedDetailAngle, setSelectedDetailAngle] = useState<'primary' | 'angle_2' | 'angle_3' | 'angle_4'>('primary');

  // Modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Partial<MasterProduct> | null>(null);
  const [isNewProduct, setIsNewProduct] = useState(false);

  // Asset Modal States
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [activeAssetProduct, setActiveAssetProduct] = useState<MasterProduct | null>(null);
  const [assetTab, setAssetTab] = useState<'upload' | 'url'>('upload');
  const [selectedAssetFile, setSelectedAssetFile] = useState<File | null>(null);
  const [assetFilePreview, setAssetFilePreview] = useState<string | null>(null);
  const [isUploadingAsset, setIsUploadingAsset] = useState<boolean>(false);
  const [uploadingAngle, setUploadingAngle] = useState<string | null>(null);
  const [newAssetUrl, setNewAssetUrl] = useState('');
  const [newAssetAngle, setNewAssetAngle] = useState<'primary' | 'angle_2' | 'angle_3' | 'angle_4'>('primary');
  const bottomFileInputRef = useRef<HTMLInputElement>(null);

  const [isPullModalOpen, setIsPullModalOpen] = useState(false);
  const [pullProduct, setPullProduct] = useState<MasterProduct | null>(null);
  const [pullBusinessId, setPullBusinessId] = useState<number>(1);
  const [pullCustomPrice, setPullCustomPrice] = useState<number>(0);
  const [pullInitialStock, setPullInitialStock] = useState<number>(20);

  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; product: MasterProduct | null }>({
    show: false,
    product: null
  });

  // Image Slider / Lightbox Modal States
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [sliderProduct, setSliderProduct] = useState<MasterProduct | null>(null);
  const [sliderActiveIndex, setSliderActiveIndex] = useState(0);

  // Keyboard navigation for image slider
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isSliderOpen || !sliderProduct) return;
      const totalImages = (sliderProduct.assets && sliderProduct.assets.length > 0)
        ? sliderProduct.assets.length
        : (sliderProduct.images?.length || 0);

      if (totalImages === 0) return;

      if (e.key === 'ArrowRight') {
        setSliderActiveIndex((prev) => (prev + 1) % totalImages);
      } else if (e.key === 'ArrowLeft') {
        setSliderActiveIndex((prev) => (prev - 1 + totalImages) % totalImages);
      } else if (e.key === 'Escape') {
        setIsSliderOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSliderOpen, sliderProduct]);

  // Fetch stats & tax rules once
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch products on filter or page change
  useEffect(() => {
    fetchProducts();
  }, [page, limit, selectedCountry, selectedCategory, selectedTaxCategory, selectedTempType]);

  const fetchInitialData = async () => {
    try {
      const [statsData, rulesData] = await Promise.all([
        masterCatalogService.getCatalogStats(),
        masterCatalogService.getTaxRules()
      ]);
      setStats(statsData);
      setTaxRules(rulesData);
    } catch (err: any) {
      console.error('Failed to load catalog metadata', err);
    }
  };

  const fetchProducts = async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      else setLoading(true);

      const data = await masterCatalogService.getProducts({
        page,
        limit,
        search,
        country: selectedCountry,
        category: selectedCategory,
        tax_category: selectedTaxCategory,
        temperature_type: selectedTempType
      });

      setProducts(data.products);
      setTotalPages(data.pagination.totalPages);
      setTotalCount(data.pagination.total);

      if (showToast) toast.success('Catalog updated!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchProducts();
  };

  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct?.sku || !editingProduct?.product_name || !editingProduct?.category) {
      toast.error('SKU, Product Name, and Category are required');
      return;
    }

    try {
      if (isNewProduct) {
        await masterCatalogService.createProduct(editingProduct);
        toast.success('Product created successfully');
      } else {
        await masterCatalogService.updateProduct(editingProduct.id!, editingProduct);
        toast.success('Product updated successfully');
      }
      setIsEditModalOpen(false);
      setEditingProduct(null);
      fetchProducts();
      fetchInitialData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async () => {
    if (!deleteModal.product) return;
    try {
      await masterCatalogService.deleteProduct(deleteModal.product.id);
      toast.success('Product removed from master catalog');
      setDeleteModal({ show: false, product: null });
      fetchProducts();
      fetchInitialData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete product');
    }
  };

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file (PNG, JPG, WEBP, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }
    setSelectedAssetFile(file);
    const previewUrl = URL.createObjectURL(file);
    setAssetFilePreview(previewUrl);
  };

  const handleDirectFileUpload = async (file: File, angle: 'primary' | 'angle_2' | 'angle_3' | 'angle_4') => {
    if (!activeAssetProduct) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file (PNG, JPG, WEBP, etc.)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be under 10MB');
      return;
    }

    try {
      setIsUploadingAsset(true);
      setUploadingAngle(angle);

      const existingAsset = activeAssetProduct.assets?.find((a) => a.angle_type === angle);

      await masterCatalogService.addProductAsset(activeAssetProduct.id, {
        file,
        angle_type: angle,
        is_primary: angle === 'primary' ? 1 : 0
      });

      if (existingAsset) {
        try {
          await masterCatalogService.deleteAsset(existingAsset.id);
        } catch {
          // Ignore delete error if replaced
        }
      }

      toast.success(`Uploaded ${angle.replace('_', ' ')} image!`);
      setSelectedAssetFile(null);
      setAssetFilePreview(null);

      const updated = await masterCatalogService.getProductById(activeAssetProduct.id);
      setActiveAssetProduct(updated);
      fetchProducts();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to upload image file');
    } finally {
      setIsUploadingAsset(false);
      setUploadingAngle(null);
    }
  };

  const handleAddAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeAssetProduct) return;

    if (assetTab === 'upload') {
      if (!selectedAssetFile) {
        toast.error('Please select an image file from your computer');
        return;
      }
      await handleDirectFileUpload(selectedAssetFile, newAssetAngle);
    } else {
      if (!newAssetUrl.trim()) {
        toast.error('Please enter an image path or URL');
        return;
      }

      try {
        setIsUploadingAsset(true);
        const existingAsset = activeAssetProduct.assets?.find((a) => a.angle_type === newAssetAngle);

        await masterCatalogService.addProductAsset(activeAssetProduct.id, {
          image_path: newAssetUrl.trim(),
          angle_type: newAssetAngle,
          is_primary: newAssetAngle === 'primary' ? 1 : 0
        });

        if (existingAsset) {
          try {
            await masterCatalogService.deleteAsset(existingAsset.id);
          } catch {
            // Ignore delete error
          }
        }

        toast.success('Image asset attached!');
        setNewAssetUrl('');
        const updated = await masterCatalogService.getProductById(activeAssetProduct.id);
        setActiveAssetProduct(updated);
        fetchProducts();
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to add asset');
      } finally {
        setIsUploadingAsset(false);
      }
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (!activeAssetProduct) return;
    try {
      await masterCatalogService.deleteAsset(assetId);
      toast.success('Asset removed');
      const updated = await masterCatalogService.getProductById(activeAssetProduct.id);
      setActiveAssetProduct(updated);
      fetchProducts();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to remove asset');
    }
  };

  const openProductDetails = async (productId: number) => {
    try {
      const fullDetails = await masterCatalogService.getProductById(productId);
      setViewingProduct(fullDetails);
      setSelectedDetailAngle('primary');
      setIsDetailsModalOpen(true);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load product details');
    }
  };

  const handlePullToStore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pullProduct) return;

    try {
      await masterCatalogService.pullToStore({
        businessId: Number(pullBusinessId),
        masterProductId: pullProduct.id,
        customPrice: Number(pullCustomPrice),
        initialStock: Number(pullInitialStock)
      });
      toast.success(`Linked "${pullProduct.product_name}" to Store #${pullBusinessId}`);
      setIsPullModalOpen(false);
      setPullProduct(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to link product to store');
    }
  };

  const handleUpdateTaxRule = async (rule: TaxRule, newRate: number, isExempt: boolean) => {
    try {
      await masterCatalogService.updateTaxRule(rule.id, {
        standard_sales_tax_rate: newRate,
        grocery_tax_exempt: isExempt ? 1 : 0
      });
      toast.success(`Updated tax rule for ${rule.state_name}`);
      const updatedRules = await masterCatalogService.getTaxRules();
      setTaxRules(updatedRules);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update tax rule');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* 1. Header & Overview Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-600">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  Master International Grocery Catalog
                </h1>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold border border-emerald-200">
                  Global Assortment
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                Centralized global grocery inventory blueprint, tax classification & multi-store distribution
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsTaxModalOpen(true)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm transition text-sm font-medium"
          >
            <Percent className="w-4 h-4 text-emerald-600" />
            Tax Engine ({taxRules.length} States)
          </button>

          <button
            onClick={() => {
              setIsNewProduct(true);
              setEditingProduct({
                sku: `RTA-GL-${Math.floor(10000 + Math.random() * 90000)}`,
                product_name: '',
                brand: '',
                country: 'India',
                category: 'Rice & Grains',
                package_size: '1 kg',
                temperature_type: 'shelf_stable',
                tax_category: 'exempt_grocery',
                suggested_retail_price: 4.99,
                cost_price: 3.49,
                wholesale_target_price: 4.09,
                estimated_gross_margin: 30.0,
                is_active: 1
              });
              setIsEditModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition text-sm font-semibold"
          >
            <Plus className="w-4 h-4" />
            Add SKU
          </button>

          <button
            onClick={() => fetchProducts(true)}
            disabled={refreshing}
            className="p-2 rounded-lg bg-white hover:bg-gray-50 text-gray-600 border border-gray-300 shadow-sm transition"
            title="Refresh Catalog"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-emerald-600' : ''}`} />
          </button>
        </div>
      </div>

      {/* 2. Top Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-xl bg-white border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Master SKUs</span>
            <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-black text-gray-900">{stats?.totalProducts.toLocaleString() || '10,228'}</span>
            <span className="text-xs text-emerald-600 font-medium">Ready for In-Platform Stores</span>
          </div>
        </div>

        <div className="p-5 rounded-xl bg-white border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Diaspora Countries</span>
            <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600">
              <Globe className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-black text-gray-900">{stats?.countryBreakdown.length || '20+'}</span>
            <span className="text-xs text-gray-500 font-medium">India, Nigeria, China, Mexico...</span>
          </div>
        </div>

        <div className="p-5 rounded-xl bg-white border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tax Classification</span>
            <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600">
              <ShieldCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-black text-emerald-600">92%</span>
            <span className="text-xs text-gray-500 font-medium">Tax-Exempt Staple Groceries</span>
          </div>
        </div>

        <div className="p-5 rounded-xl bg-white border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Gross Margin</span>
            <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-black text-gray-900">~30.0%</span>
            <span className="text-xs text-amber-600 font-medium">Wholesale/Retail Model</span>
          </div>
        </div>
      </div>

      {/* 3. Search & Filter Bar */}
      <div className="p-4 rounded-xl bg-white border border-gray-200 shadow-sm space-y-3">
        <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search products by name, brand, SKU or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-500 focus:bg-white text-sm transition"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Country filter */}
            <select
              value={selectedCountry}
              onChange={(e) => { setSelectedCountry(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm focus:outline-none focus:border-emerald-500 shadow-sm"
            >
              <option value="">All Countries ({stats?.countryBreakdown.length || '20'})</option>
              {stats?.countryBreakdown.map((c) => (
                <option key={c.country} value={c.country}>
                  {c.country} ({c.count})
                </option>
              ))}
            </select>

            {/* Category filter */}
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm focus:outline-none focus:border-emerald-500 shadow-sm"
            >
              <option value="">All Categories ({stats?.categoryBreakdown.length || '16'})</option>
              {stats?.categoryBreakdown.map((cat) => (
                <option key={cat.category} value={cat.category}>
                  {cat.category} ({cat.count})
                </option>
              ))}
            </select>

            {/* Tax category filter */}
            <select
              value={selectedTaxCategory}
              onChange={(e) => { setSelectedTaxCategory(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm focus:outline-none focus:border-emerald-500 shadow-sm"
            >
              <option value="">All Tax Types</option>
              <option value="exempt_grocery">Exempt Grocery (0% Tax)</option>
              <option value="standard_taxable">Standard Taxable (State Sales Tax)</option>
            </select>

            {/* Temperature Type */}
            <select
              value={selectedTempType}
              onChange={(e) => { setSelectedTempType(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm focus:outline-none focus:border-emerald-500 shadow-sm"
            >
              <option value="">All Storage Types</option>
              <option value="shelf_stable">Shelf Stable (Dry)</option>
              <option value="refrigerated">Refrigerated</option>
              <option value="frozen">Frozen</option>
            </select>

            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 text-sm font-semibold transition"
            >
              Filter
            </button>

            {(selectedCountry || selectedCategory || selectedTaxCategory || selectedTempType || search) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedCountry('');
                  setSelectedCategory('');
                  setSelectedTaxCategory('');
                  setSelectedTempType('');
                  setSearch('');
                  setPage(1);
                }}
                className="px-3 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 text-sm transition"
              >
                Clear
              </button>
            )}
          </div>
        </form>
      </div>

      {/* 4. Products Data Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
        {/* Top Pagination & Per Page Control Bar */}
        <div className="px-4 py-3 bg-gray-50/90 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <span>
              Showing{' '}
              <span className="text-gray-900 font-semibold">
                {totalCount === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, totalCount)}
              </span>{' '}
              of <span className="text-gray-900 font-semibold">{totalCount.toLocaleString()}</span> products
              {totalPages > 1 && (
                <span className="ml-1 text-gray-500 font-medium">
                  (Page {page} of {totalPages})
                </span>
              )}
            </span>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {/* Per Page Dropdown */}
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500 font-medium">Per page:</span>
              <select
                value={limit}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  setLimit(newLimit);
                  setPage(1);
                }}
                className="px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-800 font-semibold text-xs shadow-sm focus:outline-none focus:border-emerald-500 cursor-pointer"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-1.5">
              <button
                disabled={page <= 1 || loading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="p-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                <span className="px-2.5 py-1 text-xs font-semibold text-gray-800 bg-white rounded-md border border-gray-300 shadow-sm min-w-[28px] text-center">
                  {page}
                </span>
                <span className="text-gray-400 font-medium text-xs">/ {totalPages || 1}</span>
              </div>
              <button
                disabled={page >= totalPages || loading}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                className="p-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-700">
            <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500 border-b border-gray-200 tracking-wider">
              <tr>
                <th className="py-3.5 px-4">SKU / Origin</th>
                <th className="py-3.5 px-4">Product & Brand</th>
                <th className="py-3.5 px-4">Category / Size</th>
                <th className="py-3.5 px-4">Storage</th>
                <th className="py-3.5 px-4 text-right">Pricing (Retail / Cost)</th>
                <th className="py-3.5 px-4 text-center">Tax Category</th>
                <th className="py-3.5 px-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: Math.min(limit, 8) }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="animate-pulse">
                    <td className="py-4 px-4">
                      <div className="h-5 w-24 bg-gray-200 rounded mb-1.5" />
                      <div className="h-3 w-16 bg-gray-150 bg-gray-100 rounded" />
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
                        <div className="space-y-1.5 flex-1">
                          <div className="h-4 w-48 bg-gray-200 rounded" />
                          <div className="h-3 w-28 bg-gray-100 rounded" />
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="h-4 w-28 bg-gray-200 rounded mb-1" />
                      <div className="h-3 w-16 bg-gray-100 rounded" />
                    </td>
                    <td className="py-4 px-4">
                      <div className="h-5 w-20 bg-gray-200 rounded-full" />
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="h-4 w-16 bg-gray-200 rounded ml-auto mb-1" />
                      <div className="h-3 w-12 bg-gray-100 rounded ml-auto" />
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="h-5 w-24 bg-gray-200 rounded-full mx-auto" />
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="h-7 w-20 bg-gray-200 rounded-lg mx-auto" />
                    </td>
                  </tr>
                ))
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-gray-500">
                    <Package className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    No products matched your search filters.
                  </td>
                </tr>
              ) : (
                products.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50/70 transition">
                    {/* SKU & Origin */}
                    <td className="py-3.5 px-4">
                      <button
                        type="button"
                        onClick={() => openProductDetails(p.id)}
                        className="font-mono text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded border border-emerald-200 transition cursor-pointer"
                        title="Click to view full specs"
                      >
                        {p.sku}
                      </button>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
                        <Globe className="w-3 h-3 text-blue-500" />
                        <span>{p.country || 'International'}</span>
                      </div>
                    </td>

                    {/* Product Name, Image Avatar Stack & Brand */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-3">
                        {/* Round Image Avatar Stack (1 product image + N badge) */}
                        {(() => {
                          const imgList = (p.assets?.map((a) => a.image_path) || p.images || []).filter(Boolean);
                          const firstImg = imgList[0];
                          const extraCount = imgList.length - 1;

                          return (
                            <div
                              className="relative flex items-center shrink-0 cursor-pointer group"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const fullDetails = await masterCatalogService.getProductById(p.id);
                                const prod = fullDetails || p;
                                const hasImages = (prod.assets && prod.assets.length > 0) || (prod.images && prod.images.length > 0);
                                if (hasImages) {
                                  setSliderProduct(prod);
                                  setSliderActiveIndex(0);
                                  setIsSliderOpen(true);
                                } else {
                                  setActiveAssetProduct(prod);
                                  setIsAssetModalOpen(true);
                                }
                              }}
                              title={imgList.length > 0 ? `Click to open Image Slider (${imgList.length} Angles)` : 'Click to Upload 4-Angle Images'}
                            >
                              {firstImg ? (
                                <div className="flex items-center -space-x-2.5">
                                  <div className="w-10 h-10 rounded-full border-2 border-emerald-400 bg-white shadow-xs overflow-hidden group-hover:scale-105 group-hover:border-cyan-500 transition duration-150 flex items-center justify-center">
                                    <img
                                      src={getImageUrl(firstImg) || firstImg}
                                      alt={p.product_name}
                                      loading="lazy"
                                      decoding="async"
                                      className="w-full h-full object-contain p-0.5"
                                      onError={(e: any) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%23059669' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'%3E%3C/path%3E%3Cpolyline points='3.27 6.96 12 12.01 20.73 6.96'%3E%3C/polyline%3E%3Cline x1='12' y1='22.08' x2='12' y2='12'%3E%3C/line%3E%3C/svg%3E";
                                      }}
                                    />
                                  </div>
                                  {extraCount > 0 && (
                                    <span className="w-6 h-6 rounded-full bg-emerald-600 group-hover:bg-cyan-600 text-white font-bold text-[10px] flex items-center justify-center border-2 border-white shadow-xs transition duration-150">
                                      +{extraCount}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gray-50 border-2 border-dashed border-gray-300 group-hover:border-cyan-500 text-gray-400 group-hover:text-cyan-600 flex items-center justify-center group-hover:bg-cyan-50/40 transition duration-150">
                                  <Camera className="w-4 h-4" />
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Product Name & Brand */}
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() => openProductDetails(p.id)}
                            className="text-left font-semibold text-gray-900 hover:text-emerald-700 transition cursor-pointer block truncate max-w-xs sm:max-w-sm"
                            title={p.product_name}
                          >
                            {p.product_name}
                          </button>
                          <div className="text-xs text-gray-500 font-medium truncate max-w-xs">
                            Brand: <span className="text-gray-700 font-medium">{p.brand || 'Unbranded / Heritage'}</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Category & Package Size */}
                    <td className="py-3.5 px-4">
                      <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200">
                        {p.category}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">Size: <span className="text-gray-800 font-medium">{p.package_size || 'Standard'}</span></div>
                    </td>

                    {/* Storage Type */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <Thermometer className="w-3.5 h-3.5 text-gray-400" />
                        <span className="capitalize">{p.temperature_type.replace('_', ' ')}</span>
                      </div>
                    </td>

                    {/* Pricing */}
                    <td className="py-3.5 px-4 text-right">
                      <div className="font-bold text-gray-900 text-sm">
                        ${Number(p.suggested_retail_price).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Cost: ${Number(p.cost_price).toFixed(2)} ({p.estimated_gross_margin}%)
                      </div>
                    </td>

                    {/* Tax Category */}
                    <td className="py-3.5 px-4 text-center">
                      {p.tax_category === 'exempt_grocery' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          Tax-Exempt (0%)
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                          <Percent className="w-3 h-3 text-blue-600" />
                          Taxable (8.88%)
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-center justify-center gap-1.5">
                        {/* View Full Product Specifications */}
                        <button
                          onClick={() => openProductDetails(p.id)}
                          className="p-1.5 rounded-lg bg-gray-100 hover:bg-emerald-50 text-gray-600 hover:text-emerald-700 border border-gray-200 transition shadow-xs"
                          title="View Full Product Specifications"
                        >
                          <Eye className="w-4 h-4 text-emerald-600" />
                        </button>

                        {/* Image Asset Manager */}
                        <button
                          onClick={async () => {
                            const fullDetails = await masterCatalogService.getProductById(p.id);
                            setActiveAssetProduct(fullDetails);
                            setIsAssetModalOpen(true);
                          }}
                          className="p-1.5 rounded-lg bg-gray-100 hover:bg-cyan-50 text-gray-600 hover:text-cyan-700 border border-gray-200 transition"
                          title="Manage Images & Angles"
                        >
                          <ImageIcon className="w-4 h-4 text-cyan-600" />
                        </button>

                        {/* Pull to Store Inventory */}
                        <button
                          onClick={() => {
                            setPullProduct(p);
                            setPullCustomPrice(Number(p.suggested_retail_price));
                            setIsPullModalOpen(true);
                          }}
                          className="p-1.5 rounded-lg bg-gray-100 hover:bg-purple-50 text-gray-600 hover:text-purple-700 border border-gray-200 transition"
                          title="Pull to Platform Store"
                        >
                          <Store className="w-4 h-4 text-purple-600" />
                        </button>

                        {/* Edit */}
                        <button
                          onClick={() => {
                            setIsNewProduct(false);
                            setEditingProduct({ ...p });
                            setIsEditModalOpen(true);
                          }}
                          className="p-1.5 rounded-lg bg-gray-100 hover:bg-amber-50 text-gray-600 hover:text-amber-700 border border-gray-200 transition"
                          title="Edit Product Details"
                        >
                          <Edit3 className="w-4 h-4 text-amber-600" />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => setDeleteModal({ show: true, product: p })}
                          className="p-1.5 rounded-lg bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-700 border border-gray-200 transition"
                          title="Delete SKU"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
          <div>
            Showing{' '}
            <span className="text-gray-900 font-semibold">
              {totalCount === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, totalCount)}
            </span>{' '}
            of <span className="text-gray-900 font-semibold">{totalCount.toLocaleString()}</span> products
            {totalPages > 1 && (
              <span className="ml-1 text-gray-500 font-medium">
                (Page {page} of {totalPages})
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {/* Per Page Dropdown */}
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500 font-medium">Per page:</span>
              <select
                value={limit}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  setLimit(newLimit);
                  setPage(1);
                }}
                className="px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-800 font-semibold text-xs shadow-sm focus:outline-none focus:border-emerald-500 cursor-pointer"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-1.5">
              <button
                disabled={page <= 1 || loading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="p-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                <span className="px-2.5 py-1 text-xs font-semibold text-gray-800 bg-white rounded-md border border-gray-300 shadow-sm min-w-[28px] text-center">
                  {page}
                </span>
                <span className="text-gray-400 font-medium text-xs">/ {totalPages || 1}</span>
              </div>
              <button
                disabled={page >= totalPages || loading}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                className="p-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 5. MODAL: Edit / Create Product */}
      {isEditModalOpen && editingProduct && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-5 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                {isNewProduct ? 'Add Master SKU' : `Edit SKU: ${editingProduct.sku}`}
              </h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveProduct} className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">SKU Code *</label>
                  <input
                    type="text"
                    required
                    value={editingProduct.sku || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, sku: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 font-mono text-xs focus:bg-white focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Product Name *</label>
                  <input
                    type="text"
                    required
                    value={editingProduct.product_name || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, product_name: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Brand</label>
                  <input
                    type="text"
                    value={editingProduct.brand || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, brand: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Country of Origin</label>
                  <input
                    type="text"
                    value={editingProduct.country || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, country: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Category *</label>
                  <input
                    type="text"
                    required
                    value={editingProduct.category || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Package Size</label>
                  <input
                    type="text"
                    value={editingProduct.package_size || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, package_size: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Suggested Retail Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.suggested_retail_price || 0}
                    onChange={(e) => setEditingProduct({ ...editingProduct, suggested_retail_price: parseFloat(e.target.value) })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Cost Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.cost_price || 0}
                    onChange={(e) => setEditingProduct({ ...editingProduct, cost_price: parseFloat(e.target.value) })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Tax Classification</label>
                  <select
                    value={editingProduct.tax_category || 'exempt_grocery'}
                    onChange={(e: any) => setEditingProduct({ ...editingProduct, tax_category: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                  >
                    <option value="exempt_grocery">Exempt Grocery (0% Tax)</option>
                    <option value="standard_taxable">Standard Taxable (State Sales Tax)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Storage Type</label>
                  <select
                    value={editingProduct.temperature_type || 'shelf_stable'}
                    onChange={(e: any) => setEditingProduct({ ...editingProduct, temperature_type: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                  >
                    <option value="shelf_stable">Shelf Stable (Dry)</option>
                    <option value="refrigerated">Refrigerated</option>
                    <option value="frozen">Frozen</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={editingProduct.description || ''}
                  onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                  className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm transition"
                >
                  Save SKU
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. MODAL: Image & Asset Manager (Multi-Angle Pipeline & Computer File Upload) */}
      {isAssetModalOpen && activeAssetProduct && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 space-y-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-cyan-600" />
                  Product Images: {activeAssetProduct.product_name}
                </h2>
                <p className="text-xs text-gray-500">
                  SKU: <span className="font-mono font-semibold text-gray-700">{activeAssetProduct.sku}</span> | 4-Angle High-Resolution Storage
                </p>
              </div>
              <button
                onClick={() => {
                  setIsAssetModalOpen(false);
                  setSelectedAssetFile(null);
                  setAssetFilePreview(null);
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Current Assets Grid (4 Structured Angles) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                  4-Angle Product Showcase
                </span>
                <span className="text-xs text-gray-400">Click any empty slot to browse image</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
                {[
                  { angle: 'primary' as const, label: 'Angle 1 (Primary)' },
                  { angle: 'angle_2' as const, label: 'Angle 2 (Side)' },
                  { angle: 'angle_3' as const, label: 'Angle 3 (Detail)' },
                  { angle: 'angle_4' as const, label: 'Angle 4 (Packaging)' }
                ].map(({ angle, label }, idx) => {
                  const existing = activeAssetProduct.assets?.find((a) => a.angle_type === angle);
                  const isThisUploading = isUploadingAsset && uploadingAngle === angle;

                  return (
                    <div
                      key={angle}
                      className="p-3 rounded-xl bg-gray-50/80 border border-gray-200 text-center space-y-2 flex flex-col justify-between relative group hover:border-cyan-400 hover:shadow-sm transition"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">
                          {label}
                        </span>
                        {existing && (
                          <span className="w-2 h-2 rounded-full bg-emerald-500" title="Active asset" />
                        )}
                      </div>

                      {/* Content Box */}
                      {isThisUploading ? (
                        <div className="h-28 rounded-lg border border-dashed border-cyan-400 bg-cyan-50/50 flex flex-col items-center justify-center text-cyan-700 text-xs">
                          <Loader2 className="w-6 h-6 animate-spin mb-1 text-cyan-600" />
                          <span className="font-semibold">Uploading...</span>
                        </div>
                      ) : existing ? (
                        <div className="relative group/img h-28 rounded-lg overflow-hidden border border-gray-200 bg-white">
                          <img
                            src={getImageUrl(existing.image_path) || existing.image_path}
                            alt={label}
                            className="w-full h-full object-contain p-1"
                            onError={(e: any) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 24 24' fill='none' stroke='%23059669' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'%3E%3C/path%3E%3Cpolyline points='3.27 6.96 12 12.01 20.73 6.96'%3E%3C/polyline%3E%3Cline x1='12' y1='22.08' x2='12' y2='12'%3E%3C/line%3E%3C/svg%3E";
                            }}
                          />
                          
                          {/* Hover Overlay with Replace and Delete */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition flex items-center justify-center gap-2">
                            <label
                              className="cursor-pointer p-1.5 bg-white/90 hover:bg-white text-gray-800 rounded-lg text-xs font-semibold shadow flex items-center gap-1 transition"
                              title="Replace image from computer"
                            >
                              <Camera className="w-3.5 h-3.5 text-cyan-600" />
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleDirectFileUpload(file, angle);
                                }}
                              />
                            </label>
                            <button
                              onClick={() => handleDeleteAsset(existing.id)}
                              className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition shadow"
                              title="Delete this image"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label
                          className="h-28 rounded-lg border-2 border-dashed border-gray-300 hover:border-cyan-500 bg-white hover:bg-cyan-50/40 cursor-pointer flex flex-col items-center justify-center text-gray-500 hover:text-cyan-700 transition p-2"
                        >
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleDirectFileUpload(file, angle);
                            }}
                          />
                          <UploadCloud className="w-6 h-6 mb-1 text-gray-400 group-hover:text-cyan-600 transition" />
                          <span className="text-xs font-semibold">Browse Image</span>
                          <span className="text-[10px] text-gray-400">PNG, JPG, WEBP</span>
                        </label>
                      )}

                      <div className="text-[10px] text-gray-400 truncate">
                        {existing ? existing.image_path.split('/').pop() : 'Empty slot'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Upload & Attach Section */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
              {/* Tab Selector */}
              <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAssetTab('upload')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                      assetTab === 'upload'
                        ? 'bg-cyan-600 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <HardDrive className="w-3.5 h-3.5" />
                    Browse Computer (File Upload)
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssetTab('url')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                      assetTab === 'url'
                        ? 'bg-cyan-600 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <LinkIcon className="w-3.5 h-3.5" />
                    Image Path / Web URL
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-medium">Target Angle:</span>
                  <select
                    value={newAssetAngle}
                    onChange={(e: any) => setNewAssetAngle(e.target.value)}
                    className="p-1.5 rounded-lg bg-white border border-gray-300 text-gray-800 text-xs font-semibold focus:border-cyan-500"
                  >
                    <option value="primary">Angle 1 (Primary)</option>
                    <option value="angle_2">Angle 2 (Side)</option>
                    <option value="angle_3">Angle 3 (Detail)</option>
                    <option value="angle_4">Angle 4 (Packaging)</option>
                  </select>
                </div>
              </div>

              {/* Form Content Based on Active Tab */}
              <form onSubmit={handleAddAsset} className="space-y-3">
                {assetTab === 'upload' ? (
                  <div>
                    {/* Drag & Drop Box */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = e.dataTransfer.files?.[0];
                        if (file) handleFileSelect(file);
                      }}
                      onClick={() => bottomFileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition ${
                        selectedAssetFile
                          ? 'border-cyan-500 bg-cyan-50/30'
                          : 'border-gray-300 bg-white hover:border-cyan-400 hover:bg-cyan-50/20'
                      }`}
                    >
                      <input
                        ref={bottomFileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileSelect(file);
                        }}
                      />

                      {selectedAssetFile ? (
                        <div className="flex items-center justify-center gap-4">
                          {assetFilePreview && (
                            <img
                              src={assetFilePreview}
                              alt="Preview"
                              className="w-14 h-14 object-cover rounded-lg border border-cyan-300 shadow-sm"
                            />
                          )}
                          <div className="text-left">
                            <div className="text-xs font-bold text-gray-800 truncate max-w-xs">
                              {selectedAssetFile.name}
                            </div>
                            <div className="text-[11px] text-gray-500">
                              {(selectedAssetFile.size / 1024).toFixed(1)} KB • Ready to attach to {newAssetAngle.replace('_', ' ')}
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAssetFile(null);
                                setAssetFilePreview(null);
                              }}
                              className="text-[11px] text-red-600 hover:underline mt-0.5"
                            >
                              Choose different file
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <UploadCloud className="w-8 h-8 text-cyan-600 mx-auto" />
                          <div className="text-xs font-semibold text-gray-700">
                            Click to browse computer or drag and drop image file here
                          </div>
                          <div className="text-[11px] text-gray-400">
                            Supports PNG, JPG, JPEG, WEBP (Max 10 MB)
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      placeholder="/assets/catalog/in/rice-grains/RTA-IN-0001/primary.webp or https://images..."
                      value={newAssetUrl}
                      onChange={(e) => setNewAssetUrl(e.target.value)}
                      className="w-full p-2.5 rounded-lg bg-white border border-gray-300 text-gray-800 text-xs focus:border-cyan-500"
                    />
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-1">
                  <button
                    type="submit"
                    disabled={isUploadingAsset || (assetTab === 'upload' && !selectedAssetFile) || (assetTab === 'url' && !newAssetUrl.trim())}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs font-semibold transition shadow-sm"
                  >
                    {isUploadingAsset ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading Image...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        {assetTab === 'upload' ? 'Upload & Attach to Angle' : 'Attach Image URL'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 7. MODAL: Pull to In-Platform Store Inventory */}
      {isPullModalOpen && pullProduct && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-md p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Store className="w-5 h-5 text-emerald-600" />
                Link SKU to Store Inventory
              </h2>
              <button
                onClick={() => setIsPullModalOpen(false)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handlePullToStore} className="space-y-3 text-sm">
              <div className="p-3 rounded-lg bg-emerald-50/70 border border-emerald-100 text-xs space-y-1">
                <div className="font-semibold text-gray-900">{pullProduct.product_name}</div>
                <div className="text-gray-600">SKU: {pullProduct.sku} | Suggested Retail Price: ${Number(pullProduct.suggested_retail_price).toFixed(2)}</div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Target Business ID *</label>
                <input
                  type="number"
                  required
                  value={pullBusinessId}
                  onChange={(e) => setPullBusinessId(parseInt(e.target.value) || 1)}
                  className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Store Custom Retail Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={pullCustomPrice}
                  onChange={(e) => setPullCustomPrice(parseFloat(e.target.value) || 0)}
                  className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Initial In-Store Stock</label>
                <input
                  type="number"
                  required
                  value={pullInitialStock}
                  onChange={(e) => setPullInitialStock(parseInt(e.target.value) || 10)}
                  className="w-full p-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:bg-white focus:border-emerald-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsPullModalOpen(false)}
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm transition"
                >
                  Publish to Store
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 8. MODAL: Tax Engine Settings */}
      {isTaxModalOpen && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-xl p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Percent className="w-5 h-5 text-emerald-600" />
                US State Grocery Tax Engine Rules
              </h2>
              <button
                onClick={() => setIsTaxModalOpen(false)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {taxRules.map((rule) => (
                <div key={rule.id} className="p-3.5 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-gray-900 flex items-center gap-2">
                      {rule.state_name} ({rule.state_code})
                    </div>
                    <div className="text-xs text-gray-500">
                      Sales Tax: {(Number(rule.standard_sales_tax_rate) * 100).toFixed(3)}% | Grocery: {rule.grocery_tax_exempt ? 'Exempt (0%)' : 'Taxable'}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.001"
                      defaultValue={Number(rule.standard_sales_tax_rate)}
                      onBlur={(e) => handleUpdateTaxRule(rule, parseFloat(e.target.value) || 0.088, Boolean(rule.grocery_tax_exempt))}
                      className="w-20 p-1.5 rounded bg-white border border-gray-300 text-xs text-gray-900 text-right shadow-sm focus:border-emerald-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 9. MODAL: Delete Confirmation */}
      {deleteModal.show && deleteModal.product && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-xl max-w-sm w-full p-5 space-y-4 shadow-2xl">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="w-6 h-6" />
              <h3 className="font-bold text-gray-900">Delete Product</h3>
            </div>
            <p className="text-xs text-gray-600">
              Are you sure you want to delete <span className="font-semibold text-gray-900">"{deleteModal.product.product_name}"</span> ({deleteModal.product.sku}) from the master catalog?
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setDeleteModal({ show: false, product: null })}
                className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProduct}
                className="px-4 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition shadow-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 10. MODAL: Comprehensive Product Details & Multi-Angle Inspector */}
      {isDetailsModalOpen && viewingProduct && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50 rounded-t-2xl">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-emerald-800 bg-emerald-100/80 px-2.5 py-0.5 rounded border border-emerald-200">
                    {viewingProduct.sku}
                  </span>
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                    <Globe className="w-3 h-3 text-blue-500" />
                    {viewingProduct.country || 'International'}
                  </span>
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                    {viewingProduct.category}
                  </span>
                  {viewingProduct.is_active ? (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Active SKU
                    </span>
                  ) : (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                      Inactive
                    </span>
                  )}
                </div>
                <h1 className="text-xl font-bold text-gray-900 mt-1.5">
                  {viewingProduct.product_name}
                </h1>
                <p className="text-xs text-gray-500 font-medium">
                  Brand: <span className="text-gray-700 font-semibold">{viewingProduct.brand || 'Unbranded / Heritage'}</span>
                  {viewingProduct.package_size && ` • Size: ${viewingProduct.package_size}`}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    setActiveAssetProduct(viewingProduct);
                    setIsAssetModalOpen(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 text-xs font-semibold transition"
                  title="Upload / Manage 4 Angles"
                >
                  <Camera className="w-3.5 h-3.5 text-cyan-600" />
                  Manage Images
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    setPullProduct(viewingProduct);
                    setPullCustomPrice(Number(viewingProduct.suggested_retail_price));
                    setIsPullModalOpen(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 text-xs font-semibold transition"
                  title="Pull to Store"
                >
                  <Store className="w-3.5 h-3.5 text-purple-600" />
                  Pull to Store
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    setIsNewProduct(false);
                    setEditingProduct({ ...viewingProduct });
                    setIsEditModalOpen(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition"
                  title="Edit SKU Details"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  Edit SKU
                </button>
                <button
                  onClick={() => setIsDetailsModalOpen(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition ml-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Top Row: 4-Angle Interactive Showcase + Pricing Matrix */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* Left: 4-Angle Photo Showcase (5 Cols) */}
                <div className="md:col-span-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                      <ImageIcon className="w-4 h-4 text-cyan-600" />
                      4-Angle Visual Asset
                    </span>
                    <span className="text-[11px] font-semibold text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded border border-cyan-200 capitalize">
                      {selectedDetailAngle.replace('_', ' ')}
                    </span>
                  </div>

                  {/* Main Large Image Display */}
                  {(() => {
                    const currentAsset = viewingProduct.assets?.find(
                      (a) => a.angle_type === selectedDetailAngle
                    );
                    const primaryAsset = viewingProduct.assets?.find(
                      (a) => a.angle_type === 'primary' || a.is_primary === 1
                    );
                    const displayAsset = currentAsset || (selectedDetailAngle === 'primary' ? primaryAsset : null);

                    return (
                      <div className="h-64 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center p-3 relative overflow-hidden group">
                        {displayAsset ? (
                          <img
                            src={getImageUrl(displayAsset.image_path) || displayAsset.image_path}
                            alt={selectedDetailAngle}
                            className="w-full h-full object-contain transition group-hover:scale-105 duration-200"
                            onError={(e: any) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 24 24' fill='none' stroke='%23059669' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'%3E%3C/path%3E%3Cpolyline points='3.27 6.96 12 12.01 20.73 6.96'%3E%3C/polyline%3E%3Cline x1='12' y1='22.08' x2='12' y2='12'%3E%3C/line%3E%3C/svg%3E";
                            }}
                          />
                        ) : (
                          <div className="text-center p-4">
                            <UploadCloud className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-xs font-semibold text-gray-600">No image for {selectedDetailAngle.replace('_', ' ')}</p>
                            <button
                              type="button"
                              onClick={() => {
                                setIsDetailsModalOpen(false);
                                setActiveAssetProduct(viewingProduct);
                                setNewAssetAngle(selectedDetailAngle);
                                setIsAssetModalOpen(true);
                              }}
                              className="mt-2 text-xs font-semibold text-cyan-600 hover:underline"
                            >
                              + Upload Image Now
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Angle Switcher Thumbnails */}
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { angle: 'primary' as const, label: 'Angle 1 (Front)' },
                      { angle: 'angle_2' as const, label: 'Angle 2 (Side)' },
                      { angle: 'angle_3' as const, label: 'Angle 3 (Detail)' },
                      { angle: 'angle_4' as const, label: 'Angle 4 (Pack)' }
                    ].map(({ angle, label }) => {
                      const asset = viewingProduct.assets?.find((a) => a.angle_type === angle);
                      const isSelected = selectedDetailAngle === angle;

                      return (
                        <button
                          key={angle}
                          type="button"
                          onClick={() => setSelectedDetailAngle(angle)}
                          className={`p-1 rounded-lg border text-center transition flex flex-col items-center ${
                            isSelected
                              ? 'border-cyan-500 bg-cyan-50/50 ring-2 ring-cyan-400/30'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="w-full h-12 rounded bg-gray-50 flex items-center justify-center overflow-hidden border border-gray-100">
                            {asset ? (
                              <img
                                src={getImageUrl(asset.image_path) || asset.image_path}
                                alt={label}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <ImageIcon className="w-4 h-4 text-gray-300" />
                            )}
                          </div>
                          <span className="text-[10px] font-bold text-gray-600 mt-1 truncate max-w-full">
                            {label.split(' ')[0]} {label.split(' ')[1]}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Right: Commercial Metrics & Key Attributes (7 Cols) */}
                <div className="md:col-span-7 space-y-4">
                  {/* Financial Metrics Cards */}
                  <div>
                    <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      Commercial Pricing & Profit Matrix
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-200">
                        <div className="text-[11px] font-semibold text-emerald-800">Suggested Retail (SRP)</div>
                        <div className="text-lg font-bold text-emerald-900 mt-0.5">
                          ${Number(viewingProduct.suggested_retail_price || 0).toFixed(2)}
                        </div>
                        <div className="text-[10px] text-emerald-700">Target Shelf Price</div>
                      </div>

                      <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
                        <div className="text-[11px] font-semibold text-gray-600">Wholesale Target</div>
                        <div className="text-lg font-bold text-gray-900 mt-0.5">
                          ${Number(viewingProduct.wholesale_target_price || 0).toFixed(2)}
                        </div>
                        <div className="text-[10px] text-gray-500">B2B Base Price</div>
                      </div>

                      <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
                        <div className="text-[11px] font-semibold text-gray-600">Cost Price</div>
                        <div className="text-lg font-bold text-gray-900 mt-0.5">
                          ${Number(viewingProduct.cost_price || 0).toFixed(2)}
                        </div>
                        <div className="text-[10px] text-gray-500">Procurement Target</div>
                      </div>

                      <div className="p-3 rounded-xl bg-blue-50/70 border border-blue-200">
                        <div className="text-[11px] font-semibold text-blue-800">Gross Margin</div>
                        <div className="text-lg font-bold text-blue-900 mt-0.5">
                          +{Number(viewingProduct.estimated_gross_margin || 0).toFixed(1)}%
                        </div>
                        <div className="text-[10px] text-blue-700">
                          ${(Number(viewingProduct.suggested_retail_price || 0) - Number(viewingProduct.cost_price || 0)).toFixed(2)} profit / unit
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Core Attribute Grid */}
                  <div>
                    <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Layers className="w-4 h-4 text-purple-600" />
                      Specification Attributes
                    </h3>
                    <div className="grid grid-cols-2 gap-2.5 text-xs">
                      <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-between">
                        <span className="text-gray-500">Category / Subcategory:</span>
                        <span className="font-semibold text-gray-800">
                          {viewingProduct.category} {viewingProduct.subcategory ? `> ${viewingProduct.subcategory}` : ''}
                        </span>
                      </div>

                      <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-between">
                        <span className="text-gray-500">Package Size / Net Weight:</span>
                        <span className="font-semibold text-gray-800">{viewingProduct.package_size || 'Standard Retail Size'}</span>
                      </div>

                      <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-between">
                        <span className="text-gray-500">Storage / Temperature:</span>
                        <span className="font-semibold text-gray-800 capitalize flex items-center gap-1">
                          <Thermometer className="w-3.5 h-3.5 text-blue-500" />
                          {viewingProduct.temperature_type.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-between">
                        <span className="text-gray-500">Tax Classification:</span>
                        <span className="font-semibold text-gray-800 flex items-center gap-1">
                          {viewingProduct.tax_category === 'exempt_grocery' ? (
                            <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded font-bold text-[11px]">
                              Tax-Exempt Grocery (0%)
                            </span>
                          ) : (
                            <span className="text-blue-700 bg-blue-100 px-2 py-0.5 rounded font-bold text-[11px]">
                              Standard Taxable (8.88%)
                            </span>
                          )}
                        </span>
                      </div>

                      <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-between">
                        <span className="text-gray-500">Country of Origin:</span>
                        <span className="font-semibold text-gray-800">{viewingProduct.country || 'Global / International'}</span>
                      </div>

                      <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-between">
                        <span className="text-gray-500">Country Priority Tier:</span>
                        <span className="font-semibold text-gray-800">{viewingProduct.country_priority || 'Priority 1 (Standard)'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Row: Detailed Description, Ingredients, and Supply Chain Blueprint */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t border-gray-100">
                {/* Product Description (Col 1) */}
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-1.5">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-gray-500" />
                    Product Description
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">
                    {viewingProduct.description || 'No marketing description provided for this catalog SKU.'}
                  </p>
                </div>

                {/* Ingredients & Allergens (Col 2) */}
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-2">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Ingredients & Allergens
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold text-gray-700">Ingredients:</span>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {viewingProduct.ingredients || 'Natural ingredients, 100% authentic produce.'}
                    </p>
                  </div>
                  {viewingProduct.allergens && (
                    <div className="p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-medium">
                      ⚠️ Allergen Notice: {viewingProduct.allergens}
                    </div>
                  )}
                  {viewingProduct.storage_instructions && (
                    <div className="text-[11px] text-gray-500">
                      <span className="font-semibold">Storage:</span> {viewingProduct.storage_instructions}
                    </div>
                  )}
                </div>

                {/* Supply Chain & Rollout Strategy (Col 3) */}
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-2 text-xs">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                    Supply Chain & Rollout
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Readiness:</span>
                    <span className="font-semibold text-emerald-700">
                      {viewingProduct.production_readiness || 'Production Ready'}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Rollout Phase:</span>
                    <span className="font-semibold text-indigo-700">
                      {viewingProduct.recommended_rollout || 'Phase 1 Global Stores'}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Research Status:</span>
                    <span className="font-semibold text-gray-800">
                      {viewingProduct.research_status || 'Verified Verified'}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 text-[10px] text-gray-400 space-y-0.5">
                    <div>Created: {viewingProduct.created_at ? new Date(viewingProduct.created_at).toLocaleDateString() : 'N/A'}</div>
                    <div>Last Updated: {viewingProduct.updated_at ? new Date(viewingProduct.updated_at).toLocaleDateString() : 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/70 rounded-b-2xl">
              <span className="text-xs text-gray-500">
                SKU: <span className="font-mono font-semibold">{viewingProduct.sku}</span> | Rita Master Global Assortment
              </span>
              <button
                type="button"
                onClick={() => setIsDetailsModalOpen(false)}
                className="px-5 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold text-xs transition"
              >
                Close Inspector
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 7. Full HD Multi-Angle Product Image Slider / Lightbox Modal */}
      {isSliderOpen && sliderProduct && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col items-center justify-between p-4 md:p-6 animate-in fade-in duration-200"
          onClick={() => setIsSliderOpen(false)}
        >
          {/* Top Bar: Product Title, Angle Label & Actions */}
          <div
            className="w-full max-w-5xl flex items-center justify-between text-white py-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center font-bold text-white shadow-lg">
                <ImageIcon className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-white">{sliderProduct.product_name}</h2>
                  <span className="font-mono text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    {sliderProduct.sku}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-white/10 text-gray-300">
                    {sliderProduct.brand || 'Authentic Brand'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {sliderProduct.country || 'International'} • {sliderProduct.category} • {sliderProduct.package_size || 'Standard'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsSliderOpen(false);
                  setActiveAssetProduct(sliderProduct);
                  setIsAssetModalOpen(true);
                }}
                className="px-3.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold flex items-center gap-1.5 transition border border-white/20 cursor-pointer"
                title="Upload or manage photos for this SKU"
              >
                <Camera className="w-3.5 h-3.5 text-cyan-400" />
                <span>+ Upload / Manage</span>
              </button>
              <button
                type="button"
                onClick={() => setIsSliderOpen(false)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition cursor-pointer"
                title="Close Slider (Esc)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Center: Main Image Carousel with Prev/Next Controls */}
          {(() => {
            const sliderList: { id?: number; image_path: string; angle_type: string; is_primary?: number }[] =
              sliderProduct.assets && sliderProduct.assets.length > 0
                ? sliderProduct.assets
                : (sliderProduct.images || []).map((img, idx) => ({
                    id: idx,
                    image_path: img,
                    angle_type: idx === 0 ? 'primary' : `angle_${idx + 1}`,
                    is_primary: idx === 0 ? 1 : 0
                  }));

            const activeItem = sliderList[sliderActiveIndex] || sliderList[0];
            const totalCount = sliderList.length;

            const angleLabels: Record<string, string> = {
              primary: 'Angle 1: Front Retail Package Hero',
              angle_2: 'Angle 2: Back of Package (Nutrition & Cooking)',
              angle_3: 'Angle 3: 45° Side Gusset & Packaging Depth',
              angle_4: 'Angle 4: Top-Down Recipe Texture & Serving Bowl'
            };

            const displayAngleLabel = activeItem
              ? angleLabels[activeItem.angle_type] || `Photo ${sliderActiveIndex + 1}`
              : 'Product Photo';

            return (
              <div
                className="w-full max-w-5xl flex-1 flex flex-col items-center justify-center my-2 relative"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Main Large Image Box */}
                <div className="relative w-full h-[58vh] max-h-[600px] flex items-center justify-center rounded-2xl bg-neutral-900/90 border border-white/10 p-4 shadow-2xl overflow-hidden">
                  {/* Angle Indicator Pill */}
                  <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-black/70 backdrop-blur-md border border-white/20 text-white text-xs font-semibold flex items-center gap-2 shadow-lg">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span>{displayAngleLabel}</span>
                    <span className="text-gray-400 font-mono text-[11px]">
                      ({sliderActiveIndex + 1} of {totalCount})
                    </span>
                  </div>

                  {/* Left Prev Arrow Button */}
                  {totalCount > 1 && (
                    <button
                      type="button"
                      onClick={() => setSliderActiveIndex((prev) => (prev - 1 + totalCount) % totalCount)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/60 hover:bg-emerald-600 border border-white/20 text-white flex items-center justify-center transition shadow-xl hover:scale-110 cursor-pointer"
                      title="Previous photo (Left Arrow)"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                  )}

                  {/* Center Active Image */}
                  {activeItem && (
                    <img
                      key={activeItem.image_path}
                      src={getImageUrl(activeItem.image_path) || activeItem.image_path}
                      alt={displayAngleLabel}
                      className="max-w-full max-h-full object-contain drop-shadow-2xl transition-all duration-300 select-none p-2"
                      onError={(e: any) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 24 24' fill='none' stroke='%2310b981' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'%3E%3C/path%3E%3C/svg%3E";
                      }}
                    />
                  )}

                  {/* Right Next Arrow Button */}
                  {totalCount > 1 && (
                    <button
                      type="button"
                      onClick={() => setSliderActiveIndex((prev) => (prev + 1) % totalCount)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/60 hover:bg-emerald-600 border border-white/20 text-white flex items-center justify-center transition shadow-xl hover:scale-110 cursor-pointer"
                      title="Next photo (Right Arrow)"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  )}
                </div>

                {/* Bottom Thumbnails Strip */}
                <div className="w-full flex items-center justify-center gap-3 mt-4 overflow-x-auto py-2">
                  {sliderList.map((item, idx) => {
                    const isSelected = sliderActiveIndex === idx;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSliderActiveIndex(idx)}
                        className={`relative w-20 h-20 rounded-xl overflow-hidden border-2 transition-all shrink-0 bg-neutral-900 flex items-center justify-center p-1 cursor-pointer ${
                          isSelected
                            ? 'border-emerald-400 ring-2 ring-emerald-400 scale-105 shadow-lg shadow-emerald-500/30'
                            : 'border-white/20 opacity-60 hover:opacity-100 hover:border-white/50'
                        }`}
                        title={angleLabels[item.angle_type] || `Photo ${idx + 1}`}
                      >
                        <img
                          src={getImageUrl(item.image_path) || item.image_path}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-contain"
                        />
                        <span className="absolute bottom-1 right-1 bg-black/70 text-[10px] font-bold text-white px-1 rounded">
                          {idx + 1}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Bottom Footer Tip */}
          <div className="text-center text-xs text-gray-400 py-1" onClick={(e) => e.stopPropagation()}>
            Use <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono text-[11px]">←</kbd> and <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono text-[11px]">→</kbd> arrows to navigate photos • Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono text-[11px]">Esc</kbd> to close
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterCatalogManagement;
